import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Animated, AppState, Easing, Platform, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { usePulseValue } from "./useAnimationHelpers";

import { colors, fonts, iconTints, radius, shadows } from "@/lib/theme";

const USE_NATIVE = Platform.OS !== "web";

// AppState helper — same shape as the one in useAnimationHelpers. Duplicated
// to keep this file self-contained; the two setIntervals below pause while
// backgrounded so we don't burn CPU advancing a fake plumbing log into a
// hidden screen.
function useAppStateActive(): boolean {
    const [active, setActive] = useState(() => AppState.currentState === "active");
    useEffect(() => {
        const sub = AppState.addEventListener("change", (state) => {
            setActive(state === "active");
        });
        return () => sub.remove();
    }, []);
    return active;
}

type Cat =
    | "flag"
    | "duty"
    | "cross"
    | "section"
    | "llm"
    | "stream"
    | "parse"
    | "validate"
    | "persist"
    | "broadcast"
    | "done";

interface Message {
    cat: Cat;
    // Use **word** for accent (rendered teal+bold).
    text: string;
}

const FLAGS = [
    "BedboundOrNearBedbound",
    "MobilityImpairment",
    "CognitiveImpairment",
    "SevereVisionImpairment",
    "HearingImpairment",
    "IncontinencePresent",
    "SkinRiskOrWounds",
    "UsesAssistiveDevice",
    "HighFallRisk",
    "LivesAlone",
    "DiabetesPresent",
    "NutritionRisk",
    "FluidManagementCritical",
    "EnvironmentUnsafe",
    "HasFoley",
    "HasOstomy",
    "OralFoodIntake"
];

const DUTIES = [
    "Bath - Shower",
    "Bath - Bed Bath",
    "Bath - Total care",
    "Mouth Care / Denture Care",
    "Hair Care - Shampoo",
    "Grooming - Shave",
    "Grooming - Nails",
    "Dressing - Partial",
    "Dressing - Total",
    "Skin Care",
    "Foot Care",
    "Incontinence - Diapers",
    "Toileting - Bathroom",
    "Toileting - Commode",
    "Perineal Care",
    "Record Output - Urine",
    "Prepare Meals",
    "Meal - Breakfast",
    "Meal - Lunch",
    "Meal - Dinner",
    "Assist with Feeding",
    "Record Intake - Food",
    "Record Intake - Fluid",
    "Patient on Prescribed Diet",
    "Transferring - Hoyer Lift",
    "Transferring - Wheelchair",
    "Assist with Walking",
    "Assisted Device - Walker",
    "Assist with Home Exercise Program",
    "Assist with ROM Exercises",
    "Turning/Positioning - Every 2 hours",
    "Catheter Care",
    "Foley Bag Care",
    "Ostomy Care",
    "Remind to Take Medication",
    "Ask Patient About Pain",
    "Observe/Report Physical/Mental Changes",
    "Monitor Patient Safety",
    "Change Bed Linen",
    "Patient Laundry",
    "Light Housekeeping",
    "Shopping and Errands",
    "Accompany to Medical Appointment"
];

const SECTIONS = [
    "Vital Signs",
    "Pain",
    "Neurological",
    "EENT",
    "Cardiopulmonary",
    "Musculoskeletal",
    "GI/GU/Reproductive",
    "Integumentary",
    "Allergies and Nutrition",
    "DME and Supplies",
    "Safety, Emergency and Immunization",
    "Medications",
    "Visit Info"
];

const PHASE_1_PLUMBING: Message[] = [
    { cat: "persist", text: "Loading 17 flag definitions from poc_flag_definitions" },
    { cat: "llm", text: "Building flag-resolver system prompt" },
    { cat: "section", text: "Streaming 236 questions + answers into context" },
    { cat: "section", text: "Compressing patient diagnosis block" },
    { cat: "section", text: "Compressing medications block" },
    { cat: "llm", text: "Calling **Claude Sonnet 4.6** for flag resolution" },
    { cat: "llm", text: "Sending request to Anthropic API" },
    { cat: "stream", text: "Streaming response from Claude..." },
    { cat: "parse", text: "Parsing flag resolutions JSON" },
    { cat: "validate", text: "Validating output schema" },
    { cat: "persist", text: "Persisting 17 resolved flags to poc_flag_resolutions" },
    { cat: "persist", text: "Writing ai_call_logs audit row for **Phase 1**" },
    { cat: "done", text: "**Phase 1 complete** — moving to duty selector" }
];

const PHASE_2_PLUMBING: Message[] = [
    { cat: "persist", text: "Loading 52 effective duties for this agency" },
    { cat: "validate", text: "Filtering duties by service type (PCA / HHA)" },
    { cat: "llm", text: "Building duty-selector system prompt" },
    { cat: "llm", text: "Embedding resolved flags into user prompt" },
    { cat: "llm", text: "Calling **Claude Sonnet 4.6** for duty selection" },
    { cat: "stream", text: "Streaming response from Claude..." },
    { cat: "parse", text: "Parsing duty selections JSON" },
    { cat: "validate", text: "Cross-checking AI duty names against effective list" },
    { cat: "duty", text: "Merging AI selections with default frequencies" },
    { cat: "persist", text: "Writing ai_call_logs audit row for **Phase 2**" },
    { cat: "persist", text: "Persisting visit_plan_of_care row" },
    { cat: "persist", text: "Inserting 52 visit_poc_duties rows" },
    { cat: "done", text: "Marking poc_generation_jobs status='completed'" },
    { cat: "broadcast", text: "Broadcasting poc:generated event" }
];

function buildMessages(): Message[] {
    const out: Message[] = [];
    for (const flag of FLAGS) {
        out.push({ cat: "flag", text: `Resolving **${flag}** flag` });
        out.push({ cat: "validate", text: `Checking **${flag}** against assessment answers` });
    }
    for (const duty of DUTIES) {
        out.push({ cat: "duty", text: `Evaluating **${duty}** against findings` });
        out.push({ cat: "cross", text: `Cross-referencing **${duty}** with resolved flags` });
    }
    for (const section of SECTIONS) {
        out.push({ cat: "section", text: `Reading **${section}** section answers` });
        out.push({ cat: "section", text: `Loading question metadata for **${section}**` });
    }
    out.push(...PHASE_1_PLUMBING);
    out.push(...PHASE_2_PLUMBING);
    return out;
}

function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

const ICON_NAMES: Record<Cat, keyof typeof MaterialCommunityIcons.glyphMap> = {
    flag: "flag-outline",
    duty: "clipboard-list-outline",
    cross: "swap-horizontal",
    section: "file-document-outline",
    llm: "auto-fix",
    stream: "wave",
    parse: "code-tags",
    validate: "shield-check-outline",
    persist: "database-outline",
    broadcast: "broadcast",
    done: "check-circle-outline"
};

const ICON_TINTS: Record<Cat, { bg: string; fg: string }> = {
    flag: iconTints.teal,
    duty: iconTints.pink,
    cross: iconTints.amber,
    section: iconTints.neutral,
    llm: iconTints.purple,
    stream: iconTints.rose,
    parse: iconTints.sky,
    validate: iconTints.tealMuted,
    persist: iconTints.orange,
    broadcast: iconTints.cyan,
    done: iconTints.tealStrong
};

interface Row {
    id: number;
    message: Message;
}

const MAX_VISIBLE = 7;
const TICK_MS = 900;

function formatTime(seconds: number): string {
    const mm = Math.floor(seconds / 60);
    const ss = (seconds % 60).toString().padStart(2, "0");
    return `${mm}:${ss}`;
}

export function PocLiveActivityStream() {
    const [rows, setRows] = useState<Row[]>([]);
    const [seconds, setSeconds] = useState(0);
    const liveDotOpacity = usePulseValue(1200, 0.4, 1);
    const active = useAppStateActive();

    const deckRef = useRef<Message[]>([]);
    const deckIdxRef = useRef(0);
    const nextIdRef = useRef(0);
    const hasInitialFillRef = useRef(false);

    const drawNext = useCallback((): Message => {
        if (deckRef.current.length === 0) {
            deckRef.current = shuffle(buildMessages());
        }
        const m = deckRef.current[deckIdxRef.current % deckRef.current.length];
        deckIdxRef.current += 1;
        return m;
    }, []);

    useEffect(() => {
        // Pre-fill 4 rows on first mount only — once. Backgrounding shouldn't
        // wipe or re-fill the stream.
        if (!hasInitialFillRef.current) {
            const initial: Row[] = [];
            for (let i = 0; i < 4; i++) {
                initial.push({ id: nextIdRef.current++, message: drawNext() });
            }
            setRows(initial);
            hasInitialFillRef.current = true;
        }
        // Don't tick while backgrounded — saves CPU shuffling fake log rows
        // into a hidden screen. NOTE: the visible "Live activity" seconds
        // counter shows ELAPSED ACTIVE TIME, not wall-clock since mount.
        // After a 30s background the counter freezes at the pre-background
        // value and resumes ticking from there — this is intentionally
        // inconsistent with PocGeneratingAnimation's phase pill (which IS
        // wall-clock-derived from generationStartedAt). The stream is
        // decorative/animation, the pill is functional.
        if (!active) {
            return;
        }
        const tickHandle = setInterval(() => {
            setRows((prev) => {
                const next: Row = { id: nextIdRef.current++, message: drawNext() };
                const updated = [...prev, next];
                while (updated.length > MAX_VISIBLE) {
                    updated.shift();
                }
                return updated;
            });
        }, TICK_MS);

        const timerHandle = setInterval(() => {
            setSeconds((s) => s + 1);
        }, 1000);

        return () => {
            clearInterval(tickHandle);
            clearInterval(timerHandle);
        };
    }, [active, drawNext]);

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                <Animated.View style={[styles.liveDot, { opacity: liveDotOpacity }]} />
                <Text style={styles.label}>Live activity</Text>
                <Text style={styles.timer}>{formatTime(seconds)}</Text>
            </View>

            <View style={styles.streamArea}>
                {rows.map((row) => (
                    <StreamRow key={row.id} message={row.message} />
                ))}

                <LinearGradient
                    pointerEvents="none"
                    colors={[colors.surface, "rgba(255,255,255,0)"]}
                    style={styles.fadeTop}
                />
                <LinearGradient
                    pointerEvents="none"
                    colors={["rgba(255,255,255,0)", colors.surface]}
                    style={styles.fadeBottom}
                />
            </View>
        </View>
    );
}

interface StreamRowProps {
    message: Message;
}

function StreamRow({ message }: StreamRowProps) {
    const opacity = useRef(new Animated.Value(0)).current;
    const translateY = useRef(new Animated.Value(20)).current;

    useEffect(() => {
        Animated.parallel([
            Animated.timing(opacity, {
                toValue: 1,
                duration: 480,
                easing: Easing.out(Easing.cubic),
                useNativeDriver: USE_NATIVE
            }),
            Animated.timing(translateY, {
                toValue: 0,
                duration: 480,
                easing: Easing.out(Easing.cubic),
                useNativeDriver: USE_NATIVE
            })
        ]).start();
    }, [opacity, translateY]);

    const tint = ICON_TINTS[message.cat];

    return (
        <Animated.View style={[styles.row, { opacity, transform: [{ translateY }] }]}>
            <View style={[styles.rowIcon, { backgroundColor: tint.bg }]}>
                <MaterialCommunityIcons name={ICON_NAMES[message.cat]} size={14} color={tint.fg} />
            </View>
            <Text style={styles.rowText} numberOfLines={1} ellipsizeMode="tail">
                {renderAccents(message.text)}
            </Text>
        </Animated.View>
    );
}

function renderAccents(text: string) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
            return (
                <Text key={i} style={styles.accent}>
                    {part.slice(2, -2)}
                </Text>
            );
        }
        return <Text key={i}>{part}</Text>;
    });
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        marginHorizontal: 16,
        marginTop: 12,
        marginBottom: 16,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        overflow: "hidden",
        ...shadows.sm
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingHorizontal: 18,
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    liveDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.teal400
    },
    label: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        letterSpacing: -0.1
    },
    timer: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.textTertiary,
        fontVariant: ["tabular-nums"]
    },
    streamArea: {
        flex: 1,
        paddingVertical: 8,
        position: "relative"
    },
    row: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 12,
        paddingHorizontal: 18,
        paddingVertical: 8
    },
    rowIcon: {
        width: 26,
        height: 26,
        borderRadius: 8,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 1
    },
    rowText: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 18,
        letterSpacing: -0.1
    },
    accent: {
        color: colors.teal600,
        fontFamily: fonts.bold
    },
    fadeTop: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 24
    },
    fadeBottom: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 24
    }
});
